import Button from 'tio-ui/components/button';
import { concat } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import { and, not, or } from 'tio-ui/utilities';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import Popover from 'tio-ui/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import inputmask from '../../../modifiers/inputmask.ts';
import { getInputmaskUnmaskedValue } from '../../../utils/inputmask.ts';
import safeFormatNumber from '../../../helpers/safe-format-number.ts';
import ErrorMessages from 'tio-ui/components/error-messages';
import type Owner from '@ember/owner';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import { PhX, PhCheck, PhPencil } from 'ember-phosphor-icons';

import type SessionContextService from '../../../services/session-context.ts';
export interface TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature {
  Args: {
    approvedTotal: number;
    courseCashTotal?: number;
    application: TasApplicationModel;
    /**
     * When true, suppress the Course Cash tuition input and never write
     * CARD_FINAL_APPROVED_TOTAL — the editor edits only the reimbursement
     * FINAL_APPROVED_TOTAL. Used by the mixed-app Financial Details drawer
     * where Course Cash is edited separately in the lifecycle cards.
     */
    reimbursementOnly?: boolean;
    onSave: (
      approvedTotal: number,
      reason: string,
      approvedCourseCashTotalField?: Record<string, unknown>
    ) => void;
    onCancel?: () => void;
  };
}

export default class TuitionAssistanceApplicationDetailsEditableApplicationTotalComponent extends Component<TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature> {
  @tracked editableApprovedTotal = 0;
  @tracked editableCourseCashTotal = 0;
  @tracked isEditingApprovedTotal = false;
  @tracked reasonForEdit = '';
  @tracked submitError = null;
  @tracked didSubmit = false;

  @service declare sessionContext: SessionContextService;

  constructor(
    owner: Owner,
    args: TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature['Args']
  ) {
    super(owner, args);
    this.syncEditableApprovedTotal();
  }

  setApplicationTotal = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event);
    const approvedTotal = Math.round(Number(unmaskedValue) * 100);
    this.editableApprovedTotal = approvedTotal;
  };

  setCourseCashTotal = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event);
    const courseCashTotal = Math.round(Number(unmaskedValue) * 100);
    this.editableCourseCashTotal = courseCashTotal;
  };

  toggleIsEditing = () => {
    this.isEditingApprovedTotal = true;
  };

  cancel = () => {
    this.submitError = null;
    this.isEditingApprovedTotal = false;
    this.didSubmit = false;
    this.syncEditableApprovedTotal();
    this.args.onCancel?.();
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  saveField = dropTask(async () => {
    const additionalFields: Record<string, unknown> = {};

    if (
      this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' &&
      !this.args.reimbursementOnly
    ) {
      additionalFields['CARD_FINAL_APPROVED_TOTAL'] = this.editableCourseCashTotal;
    }
    try {
      this.submitError = null;
      this.didSubmit = true;
      await this.args.onSave(this.editableApprovedTotal, this.reasonForEdit, additionalFields);
      this.isEditingApprovedTotal = false;
      this.syncEditableApprovedTotal();
      this.didSubmit = false;
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  get isSaving() {
    return this.saveField.isRunning;
  }

  get isValid() {
    return !!this.reasonForEdit && this.editableApprovedTotal >= 0;
  }

  syncEditableApprovedTotal() {
    this.editableApprovedTotal = this.args.approvedTotal;
    this.reasonForEdit = '';
    if (
      this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' &&
      !this.args.reimbursementOnly
    ) {
      this.editableCourseCashTotal = this.args.courseCashTotal ?? 0;
    }
  }

  <template>
    <div class="tio-row-end text-right gap-4">
      <div>
        {{#if
          (and
            (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")
            (not @reimbursementOnly)
          )
        }}
          <div>
            <div class="text-sm tio-fg-muted">
              Course Cash - Tuition:
            </div>
            <div>
              {{safeFormatNumber
                (divide (or @courseCashTotal 0) 100)
                style="currency"
                currency="USD"
              }}
            </div>
          </div>
          <div>
            <div class="text-sm tio-fg-muted">
              All other expenses:
            </div>
            <div>
              {{safeFormatNumber (divide @approvedTotal 100) style="currency" currency="USD"}}
            </div>
          </div>
        {{else}}
          <div>
            {{safeFormatNumber (divide @approvedTotal 100) style="currency" currency="USD"}}
          </div>
        {{/if}}
      </div>
      <Popover
        @isOpen={{this.isEditingApprovedTotal}}
        @onOpen={{this.toggleIsEditing}}
        @onClose={{this.cancel}}
        @placement="bottom-end"
      >
        <:trigger as |pop|>
          <Button
            @appearance="outlined"
            @intent="primary"
            title={{t "common.edit"}}
            class="self-center"
            {{pop.register}}
            {{on "click" this.toggleIsEditing}}
            data-test-edit-approved-total
          >
            <PhPencil @size="20" />
          </Button>
        </:trigger>
        <:body>
          <form class="tio-col-loose p-4">
            {{#if
              (and
                (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")
                (not @reimbursementOnly)
              )
            }}
              <Input
                @value={{String (divide (or this.editableCourseCashTotal 0) 100)}}
                @onChange={{this.setCourseCashTotal}}
                {{inputmask
                  alias="currency"
                  prefix="$"
                  unmaskAsNumber=true
                  digits="2"
                  autoUnmask=true
                }}
                {{!-- disabled={{@isSubmitting}} --}}
                @description="Course Cash - Tuition"
                @classes={{hash base="mb-2"}}
              />
            {{/if}}
            <Input
              data-test-application-total-input
              @value={{String (divide @approvedTotal 100)}}
              @onChange={{this.setApplicationTotal}}
              @description={{if
                (and
                  (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")
                  (not @reimbursementOnly)
                )
                "All other expenses"
                ""
              }}
              {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
            />
            <Textarea
              data-test-reason-for-edit
              @value={{this.reasonForEdit}}
              @onChange={{this.updateReasonForEdit}}
              @label={{concat (t "common.comment") "*"}}
              @classes={{hash base="text-xs"}}
            />
            <div class="tio-flex-tight justify-end">
              <Button
                @appearance="outlined"
                @intent="warning"
                {{on "click" this.cancel}}
                disabled={{this.isSaving}}
                title="Cancel"
              >
                <PhX @size="20" />
              </Button>
              <Button
                @appearance="outlined"
                @intent="primary"
                {{on "click" this.saveField.perform}}
                @isRunning={{this.isSaving}}
                disabled={{not this.isValid}}
                title="Save"
              >
                <PhCheck @size="20" />
              </Button>
            </div>
            <ErrorMessages
              @showErrors={{this.didSubmit}}
              @error={{this.submitError}}
              class="text-xs"
            />
          </form>
        </:body>
      </Popover>
    </div>
  </template>
}
